import React, { useMemo } from 'react';
import { Row, Alert, Col, DisplayField } from '@digitools/honest-react';
import { useTranslation } from 'react-i18next';
import checkDate from '../../../utilities/checkDate';
import titleCase from '../../../utilities/titleCase';
import Card from 'src/components/Cards/Card';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import styled from 'styled-components';
import { PENDING_STATUS_CODES, DENIED_STATUS_CODES, COMPLETE_STATUS_CODES } from '../../../constants/StatusCodes';
import useStatusDataContext from 'src/packages/status/hooks/useStatusDataContext';
import getGlossaryTermById from 'src/packages/status/utilities/getGlossaryTermById';
import useTranslate from 'src/hooks/useTranslate';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import { useTooltipTracking } from 'src/hooks/useTooltipTracking';
const Error = () => (<Alert type={'warning'}>
        <span>There are no leave dates to display at this time.</span>
    </Alert>);
const StyledFlexContainerDiv = styled('div') `
    display: flex;
    flex-wrap: wrap;
    justify-content: left;
    column-gap: 1.2%;
    row-gap: 10px;
    margin-bottom: 10px;
    @media (max-width: 440px) {
        margin-bottom: 0px;
    }
`;
const StyledThirdColumnDiv = styled('div') `
  @media (min-width: 440px) {
    margin:0px 2px 0px 2px;
    flex: 0 0 32.133%;

  }
  @media (max-width: 440px) {
    flex: 0 0 49.25%;
    margin-bottom: 10px;
  }
`;
const StyledPairedColumnDiv = styled('div') `
  display: flex;
  justify-content: space-between;
  flex-wrap: no-wrap;
  column-gap: 2%;
  @media (min-width: 440px) {
    margin:0px 2px 0px 2px;
    flex: 0 0 66%;
  }
  @media (max-width: 440px) {
    flex: 0 0 100%;
    margin-bottom: 10px;
  }
`;
const StyledHalfPairedDiv = styled('div') `
    margin:0px 2px 0px 2px; 
    flex: 0 0 48.5%;
`;
const LeaveOverview = ({ leave, leaveDetails }) => {
    //Exit as early as possible if error
    if (!leaveDetails && !leave) {
        return <Error />;
    }
    const { t } = useTranslation();
    const { t: newTranslate } = useTranslate();
    const { experience } = useExperience();
    const { glossary } = useStatusDataContext();
    const { trackTooltipHover, trackTooltipClick, handleTooltipClose } = useTooltipTracking({
        eventType: TEALIUM_EVENT.EVENT_TYPE.LEAVE_OVERVIEW
    });
    const glossaryTerms = useMemo(() => {
        return {
            leaveBegin: getGlossaryTermById(glossary, '37'),
            leaveEnd: getGlossaryTermById(glossary, '40'),
            requestedBegin: getGlossaryTermById(glossary, '136'),
            requestedEnd: getGlossaryTermById(glossary, '137'),
            certifiedFrom: getGlossaryTermById(glossary, '127'),
            certifiedThrough: getGlossaryTermById(glossary, '128'),
            intermittentFrequency: getGlossaryTermById(glossary, '139'),
            intermittentMode: getGlossaryTermById(glossary, '140'),
            intermittentPeriod: getGlossaryTermById(glossary, '141'),
            takeover: getGlossaryTermById(glossary, '138'),
        };
    }, [glossary]);
    const isContinuous = leave.lveCatg === "C";
    const isIntermittent = leave.lveCatg === "I";
    const isPendingInProcessStatus = PENDING_STATUS_CODES.includes(leave.lveSttusCde);
    const isApprovedClosedStatus = COMPLETE_STATUS_CODES.includes(leave.lveSttusCde);
    const isDeniedStatus = DENIED_STATUS_CODES.includes(leave.lveSttusCde);
    var freqMode = '';
    var freqPrd = '';
    var epPrd = '';
    if (leaveDetails) {
        freqMode = leaveDetails.int_freq_mode && leaveDetails.int_freq_mode.trim();
        freqPrd = leaveDetails.int_freq_prd && leaveDetails.int_freq_prd.trim();
        epPrd = leaveDetails.int_freq_epsd_prd && leaveDetails.int_freq_epsd_prd.trim();
    }
    var periodText;
    //handle text for the period
    if (freqPrd != 'E') {
        periodText = freqPrd ? t('modeText_' + freqPrd) : '';
    }
    else {
        // handle episodes case, 'Per episode, 2 episodes per week'
        periodText = t('perEpisodeText') + ', ' + leaveDetails.int_freq_epsd_num_times + ' ';
        periodText = periodText + t('episodeText') + (leaveDetails.int_freq_epsd_num_times > 1 ? 's' : ''); //pluralize if over 1
        periodText = periodText + ' ' + t('perText') + ' ' + t('simpleModeText_' + epPrd);
    }
    return (<Card data-testid={'leaveOverview'} selectable={false} fontSize='large' small={true}>
            <StyledFlexContainerDiv>
                <StyledThirdColumnDiv>
                    <DisplayField label={titleCase(t('leaveReceivedText'))} value={leave.lveRecdDte && leave.lveRecdDte.split(' ')[0]}/>
                </StyledThirdColumnDiv>
            {(isPendingInProcessStatus || isApprovedClosedStatus) && <StyledPairedColumnDiv>
                        <StyledHalfPairedDiv>
                        <DisplayField onTooltipOpenHover={trackTooltipHover('leave begin')} onTooltipOpenClick={trackTooltipClick('leave begin')} onTooltipClose={handleTooltipClose} label={titleCase(t('leaveBeginText').toString())} value={(isPendingInProcessStatus) ? "" : checkDate(leave.lveBeginDte)} tooltip={newTranslate(glossaryTerms.leaveBegin)}/>
                        </StyledHalfPairedDiv>
                        <StyledHalfPairedDiv>
                        <DisplayField onTooltipOpenHover={trackTooltipHover('leave end')} onTooltipOpenClick={trackTooltipClick('leave end')} label={titleCase(t('leaveEndText').toString())} value={(isPendingInProcessStatus) ? "" : checkDate(leave.lveEndDte)} tooltip={newTranslate(glossaryTerms.leaveEnd)}/>
                        </StyledHalfPairedDiv>

                </StyledPairedColumnDiv>}
            
                {(isDeniedStatus || isPendingInProcessStatus) && <StyledPairedColumnDiv>
                    <StyledHalfPairedDiv>
                        <DisplayField onTooltipOpenHover={trackTooltipHover('requested to begin')} onTooltipOpenClick={trackTooltipClick('requested to begin')} onTooltipClose={handleTooltipClose} label={titleCase(t('requestedBeginText').toString())} value={checkDate(leave.lveReqBegDte)} tooltip={newTranslate(glossaryTerms.requestedBegin)}/>
                    </StyledHalfPairedDiv>
                    <StyledHalfPairedDiv>
                        <DisplayField onTooltipOpenHover={trackTooltipHover('requested to end')} onTooltipOpenClick={trackTooltipClick('requested to end')} onTooltipClose={handleTooltipClose} label={titleCase(t('requestedEndText').toString())} value={checkDate(leave.lveReqEndDte)} tooltip={newTranslate(glossaryTerms.requestedEnd)}/>
                    </StyledHalfPairedDiv>
                </StyledPairedColumnDiv>}
                {(isApprovedClosedStatus || isDeniedStatus) &&
            <StyledThirdColumnDiv>
                        <DisplayField data-private={true} label={titleCase(t('determinationDateText').toString())} value={checkDate(leave.lveDtrmDte)}/>
                    </StyledThirdColumnDiv>}

                {isContinuous &&
            <StyledThirdColumnDiv>
                        <DisplayField label={titleCase(t('lastDayWorkedText').toString())} value={checkDate(leave.lveLastWrkDte)}/>
                    </StyledThirdColumnDiv>}   
                {(isPendingInProcessStatus || isApprovedClosedStatus) && <StyledPairedColumnDiv>
                    <StyledHalfPairedDiv>
                        <DisplayField onTooltipOpenHover={trackTooltipHover('date certified from')} onTooltipOpenClick={trackTooltipClick('date certified from')} onTooltipClose={handleTooltipClose} label={titleCase(t('dateCertifiedFromText').toString())} value={(isPendingInProcessStatus) ? "" : checkDate(leave.certFromDte)} tooltip={newTranslate(glossaryTerms.certifiedFrom)}/>
                    </StyledHalfPairedDiv>
                    <StyledHalfPairedDiv>
                        <DisplayField onTooltipOpenHover={trackTooltipHover('date certified through')} onTooltipOpenClick={trackTooltipClick('date certified through')} onTooltipClose={handleTooltipClose} label={titleCase(t('dateCertifiedThroughText').toString())} value={(isPendingInProcessStatus) ? "" : checkDate(leave.certThruDte)} tooltip={newTranslate(glossaryTerms.certifiedThrough)}/>
                    </StyledHalfPairedDiv>
                </StyledPairedColumnDiv>}
                </StyledFlexContainerDiv>

                <Row>
                {(isIntermittent && (isPendingInProcessStatus || isApprovedClosedStatus)) &&
            <>
                    <Col sm={6} md={4} lg={4} size={6} className='mt-2 mt-sm-0'>
                        <DisplayField onTooltipOpenHover={trackTooltipHover('intermittent approved frequency')} onTooltipOpenClick={trackTooltipClick('intermittent approved frequency')} onTooltipClose={handleTooltipClose} label={titleCase(t('intermittentApprovedFrequencyText').toString())} value={isPendingInProcessStatus ? "" : leaveDetails && leaveDetails.approvedFrequency ? leaveDetails.int_freq_amt : null} tooltip={newTranslate(glossaryTerms.intermittentFrequency)}/>
                    </Col>
                    <Col sm={6} md={4} lg={4} size={6} className='mt-2 mt-sm-0'>
                        {/*@ts-ignore */}
                        <DisplayField onTooltipOpenHover={trackTooltipHover('intermittent approved mode')} onTooltipOpenClick={trackTooltipClick('intermittent approved mode')} onTooltipClose={handleTooltipClose} label={titleCase(t('intermittentApprovedModeText').toString())} value={isPendingInProcessStatus ? "" : (freqMode ? t('modeText_' + freqMode) : "")} tooltip={newTranslate(glossaryTerms.intermittentMode)}/>
                    </Col>
                    <Col sm={6} md={4} lg={3} size={6} className='mt-2 mt-sm-0'>
                        <DisplayField onTooltipOpenHover={trackTooltipHover('intermittent approved period')} onTooltipOpenClick={trackTooltipClick('intermittent approved period')} onTooltipClose={handleTooltipClose} label={titleCase(t('intermittentApprovedPeriodText').toString())} value={isPendingInProcessStatus ? "" : periodText} tooltip={newTranslate(glossaryTerms.intermittentPeriod)}/>
                    </Col>
                  </>}
                {experience === Experience.EMPLOYER &&
            <Col sm={6} md={4} lg={1} size={6} className='mt-2 mt-sm-0'>
                    <DisplayField onTooltipOpenHover={trackTooltipHover('takeover')} onTooltipOpenClick={trackTooltipClick('takeover')} onTooltipClose={handleTooltipClose} label={titleCase(t('takeoverText').toString())} value={leave.takeOver} tooltip={newTranslate(glossaryTerms.takeover)} style={{ textWrap: 'nowrap' }}/>
                  </Col>}
                </Row>

        </Card>);
};
export default LeaveOverview;
